import React, { useEffect, useMemo } from "react";
import { Col, Container, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestion } from "@fortawesome/free-solid-svg-icons";
import { Trans, useTranslation } from "react-i18next";
import { ng } from "@common/ngds";
import { SXDropdown } from "@common/components/ui-components/SXDropdown";
import type { NGDropdownListItem } from "@common/components/ui-components/SXDropdown";
import { useProject } from "../../features/projects/projectSlice";
import type { ConcreteFireDefaults, FireExposureSide } from "../../features/projects/projectSlice";
import { hasFireCombination } from "./shared";

const CONCRETE_FIRE_TIME_OPTIONS = [30, 60, 90, 120, 180, 240] as const;
const DEFAULT_CONCRETE_FIRE_TIME_INDEX = 1;

const toConcreteFireTimeIndex = (value: number | undefined): number => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return DEFAULT_CONCRETE_FIRE_TIME_INDEX;
  }

  const rounded = Math.round(value);
  if (rounded >= 0 && rounded < CONCRETE_FIRE_TIME_OPTIONS.length) {
    return rounded;
  }

  const minuteIndex = CONCRETE_FIRE_TIME_OPTIONS.findIndex((minutes) => minutes === rounded);
  if (minuteIndex >= 0) {
    return minuteIndex;
  }

  return DEFAULT_CONCRETE_FIRE_TIME_INDEX;
};

export function SXInputConcreteFireSettings() {
  const { t } = useTranslation();
  const {
    project: selProject,
    getProjectSectionObj,
    setFireDuration,
    setFireExposureSide,
    setFireAggregate,
    setFireManufacturing,
    setFireUse02Epsilon,
    initConcreteFireDefaults,
  } = useProject();

  const fireSettingsDisabled = !hasFireCombination(selProject);

  // A circular section (SECTION_TYPE_C, the only concrete section carrying cParams) has a single
  // face. rc-design-api's contract is that "upperSide" then means the entire perimeter, so the
  // other three flags are never used and must stay cleared.
  const sectionJson = getProjectSectionObj() as any;
  const isCircularSection = sectionJson?.cParams != null || sectionJson?.params?.type === "cParams";

  const designSettingsJson = selProject?.data?.data?.input?.structures?.[0]?.elements?.[0]?.designSettings ?? null;
  const designSettings = designSettingsJson ? ng.design.ElementDesignSettings.fromObject(designSettingsJson) : null;
  const fire = designSettings?.rc?.getColumn()?.fire;

  const nationalAnnex = selProject?.data?.data?.input?.ec?.nationalAnnex;
  const annex =
    nationalAnnex != null ? ng.eurocode.Annex_FromObject(nationalAnnex) : ng.eurocode.Annex.ANNEX_UNSPECIFIED;
  const isDenmarkAnnex = annex === ng.eurocode.Annex.ANNEX_DENMARK;

  const duration = toConcreteFireTimeIndex(fire?.fireTime);
  const upperSide = fire?.upperSide ?? false;
  const bottomSide = fire?.bottomSide ?? true;
  const leftSide = fire?.leftSide ?? true;
  const rightSide = fire?.rightSide ?? true;
  const use02Epsilon = isDenmarkAnnex ? true : (fire?.use02Epsilon ?? false);

  const aggregate =
    fire?.aggregate != null
      ? ng.design.concrete.Aggregates_FromObject(fire.aggregate)
      : ng.design.concrete.Aggregates.AGGREGATE_CALCAREOUS;
  const manufacturing =
    fire?.manufacturingRebars != null
      ? ng.design.concrete.ManufacturingType_FromObject(fire.manufacturingRebars)
      : ng.design.concrete.ManufacturingType.MANUFACTURING_TYPE_HOT_ROLLED;

  const aggregateItems = useMemo<NGDropdownListItem<ng.design.concrete.Aggregates>[]>(
    () =>
      isDenmarkAnnex
        ? [
            { id: ng.design.concrete.Aggregates.AGGREGATE_DK_CALCAREOUS, name: t("calcareous_(dk)_.label") },
            { id: ng.design.concrete.Aggregates.AGGREGATE_DK_SILICEOUS, name: t("siliceous_(dk)_.label") },
          ]
        : [
            { id: ng.design.concrete.Aggregates.AGGREGATE_CALCAREOUS, name: t("calcareous_.label") },
            { id: ng.design.concrete.Aggregates.AGGREGATE_SILICEOUS, name: t("siliceous_.label") },
          ],
    [isDenmarkAnnex, t],
  );

  const manufacturingItems = useMemo<NGDropdownListItem<ng.design.concrete.ManufacturingType>[]>(
    () => [
      {
        id: ng.design.concrete.ManufacturingType.MANUFACTURING_TYPE_HOT_ROLLED,
        name: t("hot_rolled_(q)_.label"),
      },
      {
        id: ng.design.concrete.ManufacturingType.MANUFACTURING_TYPE_QUENCHED,
        name: t("quenched_(ac)_.label", "Quenched (AC)"),
      },
      {
        id: ng.design.concrete.ManufacturingType.MANUFACTURING_TYPE_COLD_WORKED,
        name: t("cold_worked_.label"),
      },
      {
        id: ng.design.concrete.ManufacturingType.MANUFACTURING_TYPE_MIN_Q_C,
        name: t("minimum_of_q_and_c_.label", "Minimum of Q and C"),
      },
      {
        id: ng.design.concrete.ManufacturingType.MANUFACTURING_TYPE_MIN_Q_C_AC,
        name: t("minimum_of_q,_c_and_ac_.label", "Minimum of Q, C and AC"),
      },
    ],
    [t],
  );

  const durationItems = useMemo<NGDropdownListItem<number>[]>(
    () => CONCRETE_FIRE_TIME_OPTIONS.map((value, index) => ({ id: index, name: value.toString() })),
    [],
  );

  const selectedAggregate = aggregateItems.find((item) => item.id === aggregate) ?? null;
  const selectedManufacturing = manufacturingItems.find((item) => item.id === manufacturing) ?? null;
  const selectedDuration = durationItems.find((item) => item.id === duration) ?? null;

  // Fill in what the project arrived without: values it never carried, and values the current
  // national annex or section shape has made invalid. One dispatch, through an action the sync
  // middleware does not treat as an input change — repairing a project on open must not throw away
  // the calculation results it was saved with. A value the *user* changes goes through the setters
  // below and prunes the output like any other input.
  useEffect(() => {
    if (fireSettingsDisabled) {
      return;
    }

    const defaults: ConcreteFireDefaults = {};

    if (!selectedDuration) {
      defaults.fireTime = DEFAULT_CONCRETE_FIRE_TIME_INDEX;
    }

    if (!selectedAggregate && aggregateItems.length > 0) {
      defaults.aggregate = aggregateItems[0].id;
    }

    if (!selectedManufacturing && manufacturingItems.length > 0) {
      defaults.manufacturing = manufacturingItems[0].id;
    }

    if (isDenmarkAnnex && fire?.use02Epsilon !== true) {
      defaults.use02Epsilon = true;
    }

    if (isCircularSection) {
      // Collapse to the single perimeter flag. The whole patch is applied at once, so the
      // at-least-one-side guard sees the perimeter switched on and does not block the three clears.
      if (!upperSide || bottomSide || leftSide || rightSide) {
        defaults.sides = { upperSide: true, bottomSide: false, leftSide: false, rightSide: false };
      }
    } else if (!upperSide && !bottomSide && !leftSide && !rightSide) {
      // Repair projects saved before the at-least-one-side rule existed.
      defaults.sides = { bottomSide: true };
    }

    if (Object.keys(defaults).length > 0) {
      initConcreteFireDefaults(defaults);
    }
  }, [
    aggregateItems,
    fire?.use02Epsilon,
    isDenmarkAnnex,
    selectedDuration,
    selectedAggregate,
    selectedManufacturing,
    durationItems,
    manufacturingItems,
    initConcreteFireDefaults,
    fireSettingsDisabled,
    isCircularSection,
    upperSide,
    bottomSide,
    leftSide,
    rightSide,
  ]);

  const exposureRows: Array<{ key: FireExposureSide; label: string; value: boolean }> = isCircularSection
    ? [{ key: "upperSide", label: t("entire_perimeter_.label", "Entire perimeter"), value: upperSide }]
    : [
        { key: "upperSide", label: t("upper_side_.label"), value: upperSide },
        { key: "bottomSide", label: t("bottom_side_.label"), value: bottomSide },
        { key: "leftSide", label: t("left_side_.label"), value: leftSide },
        { key: "rightSide", label: t("right_side_.label"), value: rightSide },
      ];

  // The last exposed side cannot be switched off - see the reducer guard in setFireExposureSide.
  const exposedSideCount = exposureRows.filter((row) => row.value).length;

  return (
    <Container fluid id="sx-table-container" className="ps-0 px-0">
      <Container style={{ color: "#696969" }}>
        {fireSettingsDisabled && (
          <Row className="p-2 border rounded mb-2" style={{ borderColor: "var(--panel-border)" }}>
            <Col>
              <div className="mt-2" style={{ color: "#696969" }}>
                <span style={{ fontWeight: "bold" }}>{t("fire_load_combination_needs_to_be_added_.label")}</span>
              </div>
            </Col>
          </Row>
        )}

        <Row className="container-row" style={{ alignItems: "start" }}>
          <Col xs={4} className="ps-0">
            <div className="editfield-label">
              <span>{t("duration_of_fire_.label")}</span>
            </div>
            <div className="ps-0 px-0" style={{ height: 32 }}>
              <SXDropdown
                id="sx-fire-duration-dropdown"
                toggleId="sx_fire_duration_toggle"
                className="sx-dropdown"
                dataTestId="dropdown_fire_duration"
                name="fire_duration"
                lang="en"
                disabled={fireSettingsDisabled}
                list={durationItems}
                val={selectedDuration}
                onUpdate={(value) => value && setFireDuration(value.id)}
                rawNames
                localisation={false}
              />
            </div>
          </Col>

          <Col xs={3}>
            <div className="editfield-label">
              <span>{t("aggregates_.label")}</span>
            </div>
            <div className="ps-0 px-0" style={{ height: 32 }}>
              <SXDropdown
                id="sx-fire-aggregate-dropdown"
                toggleId="sx_fire_aggregate_toggle"
                className="sx-dropdown"
                dataTestId="dropdown_fire_aggregate"
                name="fire_aggregate"
                lang="en"
                disabled={fireSettingsDisabled}
                list={aggregateItems}
                val={selectedAggregate}
                onUpdate={(value) => value && setFireAggregate(value.id)}
                rawNames
                localisation={false}
              />
            </div>
          </Col>

          <Col>
            <div className="editfield-label">
              <span>{t("manufacturing_.label")}</span>
            </div>
            <div className="ps-0 px-0" style={{ height: 32 }}>
              <SXDropdown
                id="sx-fire-manufacturing-dropdown"
                toggleId="sx_fire_manufacturing_toggle"
                className="sx-dropdown"
                dataTestId="dropdown_fire_manufacturing"
                name="fire_manufacturing"
                lang="en"
                disabled={fireSettingsDisabled}
                list={manufacturingItems}
                val={selectedManufacturing}
                onUpdate={(value) => value && setFireManufacturing(value.id)}
                rawNames
                localisation={false}
              />
            </div>
          </Col>
        </Row>
      </Container>

      <Row>
        <Col>
          <span className="header">{t("exposure_.label")}</span>
        </Col>
      </Row>

      <Container className="m-0" style={{ borderRadius: 6, color: "#696969", borderColor: "var(--panel-border)" }}>
        <Row xs={12} className="container-row">
          {exposureRows.map((row) => (
            <Col xs={isCircularSection ? 12 : 3} key={row.key}>
              <Form.Check
                type="switch"
                className="sx-switch"
                id={`sx-fire-${row.key}`}
                data-testid={`sx-fire-${row.key}`}
                label={row.label}
                checked={row.value}
                disabled={fireSettingsDisabled || (row.value && exposedSideCount === 1)}
                onChange={(e) => setFireExposureSide(row.key, e.target.checked)}
              />
            </Col>
          ))}
        </Row>
      </Container>

      <Container style={{ color: "#696969" }}>
        <Row className="container-row" style={{ alignItems: "center" }}>
          <Col xs={11} className="editfield-label">
            <div className="d-inline-flex align-items-center gap-2">
              {/* Which of Figure 4.2a's ks(theta) curves the reinforcement is reduced with — worth
                  explaining, as the label is a bare symbol. Keep the hover trigger on the icon
                  only so the switch itself stays stable while being clicked. */}
              <span className="d-inline-flex">
                <Form.Check
                  type="switch"
                  id="use02Epsilon"
                  className="sx-switch"
                  label={
                    <Trans i18nKey="use_e0_2_.label" defaults="Use ε<sub>0.2</sub>" components={{ sub: <sub /> }} />
                  }
                  checked={use02Epsilon}
                  disabled={isDenmarkAnnex || fireSettingsDisabled}
                  onChange={(e) => setFireUse02Epsilon(e.target.checked)}
                />
              </span>
              <OverlayTrigger
                trigger={["hover"]}
                placement="right"
                delay={{ show: 400, hide: 200 }}
                overlay={
                  <Tooltip id="sx-fire-use-e0-2-tooltip" className="sx-tooltip-wide">
                    {t(
                      "use_e0_2_info_.label",
                      "Reduces the reinforcement strength in the fire situation with the 0.2 % proof strain curve instead of the 2 % strain curves. It falls off faster with temperature, so fire utilisation rises. Required for compression reinforcement in columns, and for tension reinforcement that cannot reach 2 % strain. See EN 1992-1-2, 4.2.4.3.",
                    )}
                  </Tooltip>
                }
              >
                <button
                  type="button"
                  aria-label={t("use_e0_2_.label", "Use ε0.2")}
                  className="info-button"
                  style={{
                    border: "1px solid #DFDFDF",
                    background: "transparent",
                    boxShadow: "none",
                    WebkitAppearance: "none",
                    appearance: "none",
                    marginLeft: 6,
                  }}
                >
                  <FontAwesomeIcon icon={faQuestion} style={{ fontSize: 10 }} />
                </button>
              </OverlayTrigger>
            </div>
          </Col>
        </Row>
      </Container>
    </Container>
  );
}
